import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Button,
  Tooltip,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Note } from '../../models/note';
import { noteApiKeyStore } from '../../api/note-api-key-store';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { columns } from './grid-columns';
import { useEffect, useState } from 'react';
import { SaveNoteFrom } from './save-note.component';
import { TabPanel } from '../../components/tab-panel';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';
import { useLoadData } from '../../hooks/useLoadData';

interface NoteRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  workOrderID?: number;
  unitID?: number;
  noteTempEntityID?: number;
  planReviewTaskID?: number;
}

interface NotesDialogProps {
  open: boolean;
  title?: string;
  noteRequestArgs: NoteRequestArgs;
  onClose: (tempID?: number) => void;
  showNoteTypes?: boolean;
}

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

export const NotesDialog = ({ open, noteRequestArgs, showNoteTypes = false, onClose }: NotesDialogProps) => {
  const [value, setValue] = useState('create');
  const [noteReqArgs, setNoteReqArgs] = useState(noteRequestArgs);

  const [newNotes, saveNewNotes] = useState<Note[]>([]);
  const [editItem, setEditItem] = useState<Note>();
  const [showTaskNotes, setShowTaskNotes] = useState(true);
  const [showProjectNotes, setShowProjectNotes] = useState(true);

  const handleClosed = () => {
    onClose(noteReqArgs.noteTempEntityID);
  };

  const handleSaveNoteCompleted = (value: Note) => {
    setNoteReqArgs({ ...noteReqArgs, noteTempEntityID: value.noteTempEntityID });
    saveNewNotes([...newNotes, value]);
    setEditItem(undefined);
    setValue('view');
    refetchNotes();
  };
  const handleEdit = (value: Note) => {
    setEditItem(value);
    setValue('edit');
  };
  const handleTabsChange = (event: any, value: any) => {
    if (value == 'view') {
      setEditItem(undefined);
      refetchNotes();
    }
    setValue(value);
  };

  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchNotes,
  } = useLoadData<{ data: Note[]; totalRecords: number }>(noteApiKeyStore.getNotes(noteReqArgs));

  const allNotes: Note[] = data?.data ?? [];
  const filteredNotes = showNoteTypes
    ? !showTaskNotes && !showProjectNotes
      ? []
      : allNotes.filter(
          (n) =>
            (showTaskNotes && n.noteSection === 'Task Note') || (showProjectNotes && n.noteSection === 'Project Note')
        )
    : allNotes;

  const gridColumns = showNoteTypes ? columns : columns.filter((c) => c.field !== 'noteSection');

  const isAddNotePermission = !useAuthorize([UserPermissionName?.ADD_NOTE]);

  useEffect(() => {
    if (isAddNotePermission) setValue('view');
    else setValue('create');
  }, [isAddNotePermission]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'lg'}
      // onClose={handleClosed}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 0, m: 0 }}>
        <Tabs value={value} onChange={handleTabsChange}>
          <Tab value="view" label={<div style={styling.tabHeading}>VIEW NOTES</div>} />
          <Tab
            value="create"
            label={<div style={styling.tabHeading}>CREATE NOTE</div>}
            hidden={isAddNotePermission || !!editItem}
          />
          <Tab value="edit" label={<div style={styling.tabHeading}>EDIT NOTE</div>} hidden={!editItem} />
        </Tabs>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <TabPanel selectedValue={value} value="view">
          <Grid container spacing={2}>
            {showNoteTypes && (
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormControlLabel
                  label="Task Notes"
                  control={
                    <Checkbox
                      size="small"
                      checked={showTaskNotes}
                      onChange={(e) => setShowTaskNotes(e.target.checked)}
                    />
                  }
                />
                <FormControlLabel
                  label="Project Notes"
                  control={
                    <Checkbox
                      size="small"
                      checked={showProjectNotes}
                      onChange={(e) => setShowProjectNotes(e.target.checked)}
                    />
                  }
                />
              </FormControl>
            </Grid>
          )}
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <SiTrackGrid
                fileTitle="note"
                dataItems={filteredNotes}
                dataItemKey="noteID"
                columns={gridColumns}
                isBusy={isLoading || isFetching}
                onEdit={handleEdit}
                deleteMutationConfig={(item) => noteApiKeyStore.deleteNote(item.noteID)}
                onDeletedItems={() => refetchNotes()}
                actionPermissions={{
                  edit: UserPermissionName?.EDIT_NOTE,
                  delete: UserPermissionName?.DELETE_NOTE,
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Grid container justifyContent="flex-end">
                <Button variant="outlined" style={{ width: '130px' }} onClick={handleClosed}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel selectedValue={value} value="create">
          <SaveNoteFrom
            noteRequestArgs={noteReqArgs}
            onSaveComplete={handleSaveNoteCompleted}
            onClosed={handleClosed}
            showNoteTypes={showNoteTypes}
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="edit">
          <SaveNoteFrom
            noteRequestArgs={noteReqArgs}
            onSaveComplete={handleSaveNoteCompleted}
            note={editItem}
            onClosed={handleClosed}
            showNoteTypes={showNoteTypes}
          />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};
